import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useEffect, useState } from 'react';
import { Control, FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StoreState } from '~/reducer';
import { surveyStoreStateSelector } from '~/selectors';
import { AppDispatch } from '~/store';
import { Optional } from '~/types';

import { answerTheSurvey } from '../data';
import { closeAnswerAndResultsDialog, fetchSurveyAnswers } from '../slice';
import { AnswerPreProcess, SurveyAnswer } from '../types';

export type UseAnswerSurveyFormDialogOutput = {
  open: boolean;
  onDialogClose: () => void;
  surveyAnswer: SurveyAnswer;
  title: Optional<string>;
  subTitle: Optional<string>;
  answerSurvey: (responses: AnswerPreProcess) => void;
  control: Control<AnswerPreProcess>;
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors<AnswerPreProcess>;
};

export const useAnswerSurveyFormDialog = (
  surveyId: string,
  handleAnswerClose: () => void,
): UseAnswerSurveyFormDialogOutput => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState<boolean>(false);
  const [title, setTitle] = useState<string>();
  const [subTitle, setSubTitle] = useState<string>();

  const surveyAnswer = useSelector((state: StoreState) => {
    return surveyStoreStateSelector(state).answerSurvey;
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const answerSurvey = React.useCallback(
    (responses: AnswerPreProcess) => {
      (async () => {
        try {
          await answerTheSurvey(surveyAnswer, responses);
          enqueueSnackbar(t('success'), {
            variant: 'success',
          });
        } catch (err) {
          enqueueSnackbar(t('laFailedToSave'), {
            variant: 'error',
          });
        }
      })();
    },
    [enqueueSnackbar, t, surveyAnswer],
  );

  const onDialogClose = React.useCallback(() => {
    handleAnswerClose();
    setOpen(false);
    (async () => {
      await dispatch(closeAnswerAndResultsDialog());
    })();
  }, [handleAnswerClose, dispatch]);

  useEffect(() => {
    setOpen(Boolean(surveyId));
  }, [surveyId]);

  useEffect(() => {
    (async () => {
      await dispatch(fetchSurveyAnswers(surveyId));
    })();
  }, [dispatch, surveyId]);

  useEffect(() => {
    if (surveyAnswer) {
      setTitle(surveyAnswer.name);
      setSubTitle(
        t('title_closes', {
          date: moment(surveyAnswer.closeDate).format('DD.MM.YYYY'),
        }),
      );
    }
  }, [surveyAnswer, t]);

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      answerSurvey(data);
      onDialogClose();
      (async () => {
        await dispatch(closeAnswerAndResultsDialog());
      })();
    });
  }, [handleSubmit, answerSurvey, onDialogClose, dispatch]);

  return {
    handleFormSubmit,
    control,
    errors,
    open,
    onDialogClose,
    surveyAnswer,
    title,
    subTitle,
    answerSurvey,
  };
};
